// ============================================================================
// FSA DEPRECIATION CALCULATION HOOK  (ADR-0006)
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { fsaDepreciationCalcService } from 'api/services/fsa/fsaDepreciationCalcService';
import type { ID } from 'api/types';
import type { DepCalcWorkspace } from 'api/types/fsa/depreciationCalc.types';

/**
 * Loads the engagement's depreciation workspace (get-or-create on the
 * backend). The tab owns its own editable draft; this hook only handles
 * the fetch lifecycle.
 */
export const useFsaDepreciationCalc = (engagementId: ID | null) => {
  const [workspace, setWorkspace] = useState<DepCalcWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkspace = useCallback(async () => {
    if (!engagementId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fsaDepreciationCalcService.getWorkspace(engagementId);
      setWorkspace(r.data ?? null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load depreciation workspace.');
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  return { workspace, setWorkspace, loading, error, refetch: fetchWorkspace };
};

export default useFsaDepreciationCalc;
